import React, { useEffect, useState } from "react";
import JumboListNoDataPlaceHolder from "@jumbo/components/JumboList/components/JumboListNoDataPlaceHolder";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import {
  Card,
  IconButton,
  Tooltip,
  Typography,
  Stack,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import CategoryItem from "./CategoryItem";
import { useJumboDialog } from "@jumbo/components/JumboDialog/hooks/useJumboDialog";
import JumboSearch from "@jumbo/components/JumboSearch";
import AddBoxIcon from "@mui/icons-material/AddBox";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  deleteDSOKCategory,
  exportDSOKCategoriesCsv,
  getCustomer,
  getDSOKCategories,
  previewDSOKCategoriesImportCsv,
  commitDSOKCategoriesImportCsv,
} from "app/services/api/management";
import CsvImportDialog from "../components/CsvImportDialog";

// removed direct baseURL manipulation; APIs set it internally
import { prepareCategoryLevels } from "app/utils/arrayHelpers";

const DSOKCategories = () => {
  const { getAuthUser } = useJumboAuth();
  const {
    customer: { url },
  } = getAuthUser();

  const { t, i18n } = useTranslation();
  const { showDialog, hideDialog } = useJumboDialog();
  const navigate = useNavigate();
  const [searchName, setSearchName] = useState();

  const [categories, setCategories] = useState([]);
  const [defaultCategories, setDefaultCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [customer, setCustomer] = useState();
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const defaultLanguage =
    customer?.dsokSettings?.customer?.defaultLanguage || i18n.language;
  const languages = customer?.dsokSettings?.customer?.languages || [];
  const customerUrl = url || "";

  const loadCategories = async () => {
    setLoading(true);
    await getDSOKCategories().then((categories) => {
      // const { categories } = response || {}
      const categoryArr = Object.keys(categories || {}).map((id) => ({
        ...categories[id],
      }));
      const leveledCategories = prepareCategoryLevels(categoryArr || []);

      const query = (searchName || "").toLowerCase();
      const filteredCategories = query
        ? leveledCategories.filter((category) => {
            const localizedName =
              category?.name?.[defaultLanguage] ??
              category?.name?.[i18n.language] ??
              "";
            return String(localizedName).toLowerCase().includes(query);
          })
        : leveledCategories;

      setCategories(filteredCategories || []);
      setDefaultCategories(leveledCategories || []);
    });
    setLoading(false);
  };

  const searchByName = async (searchName) => {
    setLoading(true);
    if (searchName !== undefined) await loadCategories();
  };

  useEffect(() => {
    searchByName(searchName);
  }, [searchName]);

  const loadCustomer = async () => {
    await getCustomer(customerUrl).then((customer) => setCustomer(customer));
  };

  useEffect(() => {
    loadCustomer();
  }, []);

  const handleDelete = async (category) => {
    hideDialog();
    setLoading(true);
    try {
      await deleteDSOKCategory(category._id);
      await loadCategories();
      const message = `${t("buttons.delete")} ${t(
        "notifications.wasSuccessful"
      )}`;
      enqueueSnackbar(message, {
        variant: "success",
      });
    } catch (error) {
      const message = `${t("buttons.delete")} ${t("notifications.wasFailed")}`;
      enqueueSnackbar(message, {
        variant: "error",
      });
      setLoading(false);
    }
  };

  const handleExportCategories = async () => {
    try {
      const blob = await exportDSOKCategoriesCsv();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "categories.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      enqueueSnackbar(t("notifications.wasFailed"), { variant: "error" });
    }
  };

  const handleEditCategory = (category) => {
    navigate(`/customer/dsok/categories/${category._id}`);
  };

  const deleteConfirmation = (category) => {
    showDialog({
      variant: "confirm",
      title: t("widgets.confirmDialog.areYouSure"),
      onYes: () => handleDelete(category),
      onNo: () => hideDialog(),
    });
  };

  return (
    <React.Fragment>
      <Typography variant={"h2"} mb={3}>
        {t("pages.dsokCategories.title")}
      </Typography>
      <Stack
        direction="row"
        display="flex"
        alignItems="center"
        mb={2}
        spacing={1}
      >
        <Card sx={{ mb: 0, width: "50%" }}>
          <JumboSearch
            page="DSOKCategories"
            onChange={setSearchName}
            sx={{
              width: "100%",
            }}
            placeholder={t("pages.presentation.searchPresentation")}
          />
        </Card>
        <Tooltip title={t("pages.articleCategories.addCategory")}>
          <IconButton onClick={() => navigate("/customer/dsok/categories/new")}>
            <AddBoxIcon />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={t("pages.csvImportExport.exportCsv", {
            defaultValue: "Exportovať do CSV",
          })}
        >
          <IconButton onClick={handleExportCategories}>
            <FileDownloadIcon />
          </IconButton>
        </Tooltip>
        <Tooltip
          title={t("pages.csvImportExport.importCsv", {
            defaultValue: "Importovať z CSV",
          })}
        >
          <IconButton onClick={() => setImportDialogOpen(true)}>
            <UploadFileIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      {loading ? (
        <CircularProgress sx={{ m: "5px 50%" }} />
      ) : (
        <>
          {categories?.length ? (
            categories.map((category, index) => (
              <CategoryItem
                key={index}
                item={category}
                disabled={loading}
                onDelete={deleteConfirmation}
                onEdit={() => handleEditCategory(category)}
                productLanguages={languages}
                defaultLanguage={defaultLanguage}
                categories={defaultCategories}
                searchName={searchName}
              />
            ))
          ) : (
            <JumboListNoDataPlaceHolder />
          )}
        </>
      )}
      <CsvImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        title={t("pages.csvImportExport.importCategoriesTitle", {
          defaultValue: "Import kategórií (CSV)",
        })}
        previewFn={previewDSOKCategoriesImportCsv}
        commitFn={commitDSOKCategoriesImportCsv}
        onImported={loadCategories}
      />
    </React.Fragment>
  );
};

export default DSOKCategories;
